import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { LockManagerApi } from './LockManagerApi';
import {
  Lock,
  CreateLockRequest,
  AffectedLocks,
  LockActionRequest,
} from './types';

export class LockManagerClient implements LockManagerApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('proxy');
  }

  async getLocks(): Promise<Lock[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/lock-manager/locks`);

    if (!response.ok) {
      throw new Error(`Failed to fetch locks: ${response.statusText}`);
    }

    return await response.json();
  }

  async createLock(request: CreateLockRequest): Promise<Lock> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/lock-manager/locks`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to create lock: ${response.statusText}`);
    }

    return await response.json();
  }

  async lockLock(lockName: string, request?: LockActionRequest): Promise<Lock> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/lock-manager/locks/${encodeURIComponent(
        lockName,
      )}/lock?source=manual`,
      {
        method: 'POST',
        ...(request && {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(request),
        }),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to lock ${lockName}: ${response.statusText}`);
    }

    return await response.json();
  }

  async unlockLock(
    lockName: string,
    request?: LockActionRequest,
  ): Promise<Lock> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/lock-manager/locks/${encodeURIComponent(
        lockName,
      )}/unlock?source=manual`,
      {
        method: 'POST',
        ...(request && {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(request),
        }),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to unlock ${lockName}: ${response.statusText}`);
    }

    return await response.json();
  }

  async lockAll(request?: LockActionRequest): Promise<AffectedLocks> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/lock-manager/locks/lock?source=manual`,
      {
        method: 'POST',
        ...(request && {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(request),
        }),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to lock all: ${response.statusText}`);
    }

    return await response.json();
  }

  async unlockAll(request?: LockActionRequest): Promise<AffectedLocks> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/lock-manager/locks/unlock?source=manual`,
      {
        method: 'POST',
        ...(request && {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(request),
        }),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to unlock all: ${response.statusText}`);
    }

    return await response.json();
  }
}
